import React, { useState } from "react";
import { Accordion, AccordionGroup } from "@outpave/ds";

export interface RequirementSection {
  key: string;
  title: React.ReactNode;
  /** The status glyph, which keeps reporting while the section is closed. */
  icon: React.ReactNode;
  needsAction: boolean;
  content: React.ReactNode;
}

/**
 * Opens when an action appears, never closes on its own: resolving the action
 * would otherwise snap the section shut on what the user just did.
 */
function useActionExpanded(needsAction: boolean, recordId?: string) {
  const [expanded, setExpanded] = useState(needsAction);
  const [seen, setSeen] = useState({ recordId, needsAction });

  if (seen.recordId !== recordId || seen.needsAction !== needsAction) {
    const isNewRecord = seen.recordId !== recordId;
    setSeen({ recordId, needsAction });
    if (isNewRecord || needsAction) setExpanded(needsAction);
  }

  return {
    expanded,
    onChange: (_event: React.SyntheticEvent, isExpanded: boolean) =>
      setExpanded(isExpanded),
  };
}

const RequirementAccordion: React.FC<{
  section: RequirementSection;
  recordId?: string;
}> = ({ section, recordId }) => {
  const expansion = useActionExpanded(section.needsAction, recordId);
  return (
    <Accordion title={section.title} icon={section.icon} {...expansion}>
      {section.content}
    </Accordion>
  );
};

interface RequirementSectionsProps {
  sections: RequirementSection[];
  /** A new record starts over from its own sections' needs. */
  recordId?: string;
}

/** A Requirements tab's sections as package accordions (D14). */
export const RequirementSections: React.FC<RequirementSectionsProps> = ({
  sections,
  recordId,
}) => (
  <AccordionGroup>
    {sections.map((section) => (
      <RequirementAccordion
        key={section.key}
        section={section}
        recordId={recordId}
      />
    ))}
  </AccordionGroup>
);
